import React from "react";
import { Link } from "react-router-dom";
import TrustLayout, { H2 } from "./TrustLayout";

export default function Methodology() {
  return (
    <TrustLayout
      meta={{
        path: "/methodology",
        kicker: "How the numbers work",
        title: "Calculation Methodology & Data Sources | Centennial",
        description:
          "Exactly how the Centennial retirement calculator works: Monte Carlo simulation design, live market data sources, historical return priors, tax and healthcare assumptions, and known limitations.",
        h1: "Calculation Methodology",
        reviewed: "June 2026",
      }}
    >
      <p>
        This page explains exactly how the <Link className="text-[#2B5339] underline" to="/">Centennial
        calculator</Link> produces its numbers, what data it uses, and where its limits are. No
        black boxes.
      </p>

      <H2>Monte Carlo simulation</H2>
      <p>
        Each projection runs thousands of independent simulations of your plan (5,000 in the
        standard run). In every simulation, each year's market return is drawn randomly from a
        distribution calibrated to long-run historical behavior of stocks and bonds. Your
        contributions, withdrawals, income streams, and taxes are applied year by year until your
        chosen life expectancy — up to age 100 and beyond. The "probability of success" is simply
        the percentage of simulations in which your money outlives you.
      </p>

      <H2>Market data</H2>
      <p>
        Live index prices and recent volatility come from Yahoo Finance (S&amp;P 500, total bond
        market, and international indices). Long-run return assumptions are anchored to
        historical priors — roughly a century of US equity and bond data — rather than recent
        performance alone. You can see and override the assumptions inside the calculator.
      </p>

      <H2>Historical stress tests</H2>
      <p>
        In addition to random simulations, your plan is replayed through four of the hardest
        real-world sequences on record: the 1929 Great Depression, 1973–74 stagflation, the 2000
        dot-com "lost decade", and the 2008 global financial crisis. These use actual historical
        return sequences, not random draws.
      </p>

      <H2>Taxes, healthcare, and income</H2>
      <p>
        Withdrawals can be modeled across Taxable, 401(k)/Traditional, and Roth buckets with
        tax-aware ordering and optional Roth conversion ladders. Healthcare costs can inflate
        faster than general inflation (medical CPI has historically outpaced headline CPI —
        source: US Bureau of Labor Statistics). Social Security and pension streams support
        custom start ages and inflation adjustment; claiming-age tradeoffs follow Social
        Security Administration rules.
      </p>

      <H2>Everything is in real dollars</H2>
      <p>
        All projections are displayed in inflation-adjusted (real) dollars, so a balance shown
        for age 90 has the purchasing power of today's money.
      </p>

      <H2>Known limitations</H2>
      <p>
        No simulation predicts the future. Return distributions are estimates; actual sequences
        can be worse than anything in the historical record. Tax modeling is simplified (no state
        taxes, no IRMAA). This tool is educational — it does not replace personalized advice from
        a fiduciary advisor or tax professional. See our <Link className="text-[#2B5339] underline" to="/disclaimer">full disclaimer</Link>.
      </p>

      <H2>Primary sources we rely on</H2>
      <p>
        Social Security Administration (ssa.gov) for claiming rules · IRS (irs.gov) for
        contribution limits and Roth rules · Medicare (medicare.gov) for enrollment ages · US
        Bureau of Labor Statistics (bls.gov) for inflation data · William Bengen's 1994 SAFEMAX
        research and the Trinity Study for withdrawal-rate foundations.
      </p>
    </TrustLayout>
  );
}
